import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetSpecialOfferQuery,
  useUpdateSpecialOfferMutation,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import ImageUpload from "../components/ImageUpload";
import Icon from "../components/Icon";
import RichTextEditor from "../components/RichTextEditor";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";
import { useLanguage } from "../contexts/LanguageContext";

const EditSpecialOffer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  // RTK Query hooks
  const { data: offerData, isLoading: loading } = useGetSpecialOfferQuery(id);
  const [updateOffer, { isLoading: saving }] = useUpdateSpecialOfferMutation();

  const [saveStatus, setSaveStatus] = useState("");
  const messageType = useMessageType(saveStatus);

  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    content: "",
    contentAr: "",
    image: null,
    is_active: true,
    keywords: "",
    meta_title: "",
    meta_description: "",
    cta_title: "Interested in This Offer?",
    cta_title_ar: "مهتم بهذا العرض؟",
    cta_description: "Contact us today to learn more about this special offer and how it can benefit your business.",
    cta_description_ar: "اتصل بنا اليوم لمعرفة المزيد عن هذا العرض الخاص وكيف يمكن أن يفيد عملك.",
    cta_button1_text: "Contact Us Now",
    cta_button1_text_ar: "اتصل بنا الآن",
    cta_button2_text: "View All Offers",
    cta_button2_text_ar: "عرض جميع العروض",
    cta_button1_link: "/#contact",
    cta_button2_link: "/offers",
  });

  const [originalOffer, setOriginalOffer] = useState(null);

  // Load offer data when fetched
  useEffect(() => {
    if (offerData && offerData.offer) {
      const offer = offerData.offer;
      setOriginalOffer(offer);
      setFormData({
        title: offer.title || "",
        titleAr: offer.title_ar || "",
        content: offer.content || "",
        contentAr: offer.content_ar || "",
        image: offer.image_url || null,
        is_active: offer.is_active !== false,
        keywords: offer.keywords || "",
        meta_title: offer.meta_title || "",
        meta_description: offer.meta_description || "",
        cta_title: offer.cta_title || "Interested in This Offer?",
        cta_title_ar: offer.cta_title_ar || "مهتم بهذا العرض؟",
        cta_description: offer.cta_description || "Contact us today to learn more about this special offer and how it can benefit your business.",
        cta_description_ar: offer.cta_description_ar || "اتصل بنا اليوم لمعرفة المزيد عن هذا العرض الخاص وكيف يمكن أن يفيد عملك.",
        cta_button1_text: offer.cta_button1_text || "Contact Us Now",
        cta_button1_text_ar: offer.cta_button1_text_ar || "اتصل بنا الآن",
        cta_button2_text: offer.cta_button2_text || "View All Offers",
        cta_button2_text_ar: offer.cta_button2_text_ar || "عرض جميع العروض",
        cta_button1_link: offer.cta_button1_link || "/#contact",
        cta_button2_link: offer.cta_button2_link || "/offers",
      });
    }
  }, [offerData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("addEditOffer.pleaseLoginToSave"));
      return;
    }

    // Validate form
    if (!formData.title.trim()) {
      setSaveStatus(t("addEditOffer.pleaseEnterTitle"));
      return;
    }

    if (!formData.content.trim()) {
      setSaveStatus(t("addEditOffer.pleaseEnterContent"));
      return;
    }

    setSaveStatus("");

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("titleAr", formData.titleAr);
      submitData.append("content", formData.content);
      submitData.append("contentAr", formData.contentAr);
      submitData.append("is_active", formData.is_active ? "1" : "0");
      submitData.append("keywords", formData.keywords || "");
      submitData.append("meta_title", formData.meta_title || "");
      submitData.append("meta_description", formData.meta_description || "");
      submitData.append("cta_title", formData.cta_title || "");
      submitData.append("cta_title_ar", formData.cta_title_ar || "");
      submitData.append("cta_description", formData.cta_description || "");
      submitData.append("cta_description_ar", formData.cta_description_ar || "");
      submitData.append("cta_button1_text", formData.cta_button1_text || "");
      submitData.append("cta_button1_text_ar", formData.cta_button1_text_ar || "");
      submitData.append("cta_button2_text", formData.cta_button2_text || "");
      submitData.append("cta_button2_text_ar", formData.cta_button2_text_ar || "");
      submitData.append("cta_button1_link", formData.cta_button1_link || "");
      submitData.append("cta_button2_link", formData.cta_button2_link || "");

      // Only append image if it's a new file (not existing URL)
      if (formData.image && typeof formData.image === "object") {
        submitData.append("image", formData.image);
      }

      const result = await updateOffer({ id, body: submitData }).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditOffer.offerUpdatedSuccessfully"));
        setTimeout(() => {
          navigate(getLocalizedPath("/dashboard/special-offers"));
        }, 1500);
      } else {
        setSaveStatus(t("addEditOffer.errorUpdatingOffer"));
      }
    } catch (error) {
      console.error("Error updating offer:", error);
      setSaveStatus(
        error.data?.message || t("addEditOffer.errorUpdatingOffer")
      );
    }
  };

  const handleCancel = () => {
    navigate(getLocalizedPath("/dashboard/special-offers"));
  };

  if (loading) {
    return (
      <div className="add-special-offer">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("addEditOffer.loadingOffer")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="add-special-offer">
        <div className="auth-required">
          <h2>{t("addEditOffer.authenticationRequired")}</h2>
          <p>{t("addEditOffer.pleaseLoginToEdit")}</p>
          <button
            className="login-btn"
            onClick={() => navigate(getLocalizedPath("/login"))}
          >
            {t("addEditOffer.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (!originalOffer) {
    return (
      <div className="add-special-offer">
        <div className="auth-required">
          <h2>{t("addEditOffer.offerNotFound")}</h2>
          <p>{t("addEditOffer.offerNotFoundMessage")}</p>
          <button
            className="login-btn"
            onClick={() => navigate(getLocalizedPath("/dashboard/special-offers"))}
          >
            {t("addEditOffer.backToOffers")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-special-offer">
      <div className="page-header">
        <div className="header-content">
          <button type="button" className="back-btn" onClick={handleCancel}>
            <Icon name="arrow-left" size="16px" />
            {t("addEditOffer.backToOffers")}
          </button>
          <h1>{t("addEditOffer.editSpecialOffer")}</h1>
          <p>{t("addEditOffer.updateOfferDetails")}</p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">
          <div className="form-content">
            <div className="form-group">
              <label>{t("addEditOffer.offerTitleEnglish")} *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("addEditOffer.enterOfferTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.offerTitleArabic")}</label>
              <input
                type="text"
                value={formData.titleAr}
                onChange={(e) => handleInputChange("titleAr", e.target.value)}
                placeholder={t("addEditOffer.enterOfferTitleArabic")}
                className="form-input"
                dir="rtl"
              />
            </div>

            <div className="form-group">
              <ImageUpload
                label={t("addEditOffer.offerImage")}
                value={formData.image}
                onChange={(file) => handleInputChange("image", file)}
                className="image-upload-modern"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.offerContentEnglish")} *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => handleInputChange("content", content)}
                placeholder={t("addEditOffer.enterOfferDetails")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.offerContentArabic")}</label>
              <RichTextEditor
                value={formData.contentAr}
                onChange={(content) => handleInputChange("contentAr", content)}
                placeholder={t("addEditOffer.enterOfferDetailsArabic")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.keywords")}</label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => handleInputChange("keywords", e.target.value)}
                placeholder={t("addEditOffer.enterKeywords")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditOffer.keywordsHint")}
              </small>
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.metaTitle")}</label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => handleInputChange("meta_title", e.target.value)}
                placeholder={t("addEditOffer.enterMetaTitle")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditOffer.metaTitleHint")}
              </small>
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.metaDescription")}</label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => handleInputChange("meta_description", e.target.value)}
                placeholder={t("addEditOffer.enterMetaDescription")}
                className="form-input"
                rows="3"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditOffer.metaDescriptionHint")}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                />
                <span>{t("addEditOffer.activeOffer")}</span>
              </label>
            </div>

            {/* CTA Section */}
            <div className="form-section-divider">
              <h3>{t("addEditOffer.ctaSection")}</h3>
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaTitle")}</label>
              <input
                type="text"
                value={formData.cta_title}
                onChange={(e) => handleInputChange("cta_title", e.target.value)}
                placeholder={t("addEditOffer.enterCtaTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaTitleAr")}</label>
              <input
                type="text"
                value={formData.cta_title_ar}
                onChange={(e) => handleInputChange("cta_title_ar", e.target.value)}
                placeholder={t("addEditOffer.enterCtaTitleAr")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaDescription")}</label>
              <textarea
                value={formData.cta_description}
                onChange={(e) => handleInputChange("cta_description", e.target.value)}
                placeholder={t("addEditOffer.enterCtaDescription")}
                className="form-input"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaDescriptionAr")}</label>
              <textarea
                value={formData.cta_description_ar}
                onChange={(e) => handleInputChange("cta_description_ar", e.target.value)}
                placeholder={t("addEditOffer.enterCtaDescriptionAr")}
                className="form-input"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaButton1Text")}</label>
              <input
                type="text"
                value={formData.cta_button1_text}
                onChange={(e) => handleInputChange("cta_button1_text", e.target.value)}
                placeholder={t("addEditOffer.enterCtaButton1Text")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaButton1TextAr")}</label>
              <input
                type="text"
                value={formData.cta_button1_text_ar}
                onChange={(e) => handleInputChange("cta_button1_text_ar", e.target.value)}
                placeholder={t("addEditOffer.enterCtaButton1TextAr")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaButton2Text")}</label>
              <input
                type="text"
                value={formData.cta_button2_text}
                onChange={(e) => handleInputChange("cta_button2_text", e.target.value)}
                placeholder={t("addEditOffer.enterCtaButton2Text")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaButton2TextAr")}</label>
              <input
                type="text"
                value={formData.cta_button2_text_ar}
                onChange={(e) => handleInputChange("cta_button2_text_ar", e.target.value)}
                placeholder={t("addEditOffer.enterCtaButton2TextAr")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaButton1Link")}</label>
              <input
                type="text"
                value={formData.cta_button1_link}
                onChange={(e) => handleInputChange("cta_button1_link", e.target.value)}
                placeholder={t("addEditOffer.enterCtaButton1Link")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditOffer.ctaButtonLinkHint")}
              </small>
            </div>

            <div className="form-group">
              <label>{t("addEditOffer.ctaButton2Link")}</label>
              <input
                type="text"
                value={formData.cta_button2_link}
                onChange={(e) => handleInputChange("cta_button2_link", e.target.value)}
                placeholder={t("addEditOffer.enterCtaButton2Link")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditOffer.ctaButtonLinkHint")}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus("")}
        />
        <div className="save-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            {t("addEditOffer.cancel")}
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                {t("addEditOffer.updating")}
              </>
            ) : (
              <>
                <Icon name="save" size="16px" />
                {t("addEditOffer.updateOffer")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditSpecialOffer;
